import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import * as XLSX from 'xlsx'
import PptxGenJS from 'pptxgenjs'
import { SprintReport, SprintDetails } from '../pages/SprintReport'

export class ExportService {
  
  // PDF Export
  async exportToPDF(report: SprintReport, sprintDetails: SprintDetails): Promise<void> {
    try {
      console.log('📄 Generating PDF export...')
      
      const pdf = new jsPDF('p', 'mm', 'a4')
      const pageWidth = pdf.internal.pageSize.getWidth()
      const margin = 20
      const contentWidth = pageWidth - (margin * 2)
      let yPosition = margin

      // Title
      pdf.setFontSize(24)
      pdf.setFont('helvetica', 'bold')
      pdf.text('Sprint Report', margin, yPosition)
      yPosition += 15

      // Sprint Details
      pdf.setFontSize(12)
      pdf.setFont('helvetica', 'normal')
      pdf.text(`Sprint: ${sprintDetails.label}`, margin, yPosition)
      yPosition += 8
      pdf.text(`Duration: ${sprintDetails.duration}`, margin, yPosition)
      yPosition += 8
      pdf.text(`Goal: ${sprintDetails.goal}`, margin, yPosition)
      yPosition += 15

      // Summary Section
      pdf.setFontSize(16)
      pdf.setFont('helvetica', 'bold')
      pdf.text('Sprint Summary', margin, yPosition)
      yPosition += 10

      pdf.setFontSize(10)
      pdf.setFont('helvetica', 'normal')
      pdf.text(`Total Cards: ${report.summary.totalCards}`, margin, yPosition)
      yPosition += 6
      pdf.text(`Completed Cards: ${report.summary.completedCards}`, margin, yPosition)
      yPosition += 6
      pdf.text(`Incomplete Cards: ${report.summary.incompleteCards}`, margin, yPosition)
      yPosition += 6
      pdf.text(`Velocity: ${report.summary.velocity} cards/day`, margin, yPosition)
      yPosition += 15

      // Insights Section
      if (report.insights.bottlenecks.length > 0) {
        pdf.setFontSize(14)
        pdf.setFont('helvetica', 'bold')
        pdf.text('Bottlenecks', margin, yPosition)
        yPosition += 8

        pdf.setFontSize(10)
        pdf.setFont('helvetica', 'normal')
        report.insights.bottlenecks.forEach(bottleneck => {
          pdf.text(`• ${bottleneck}`, margin + 5, yPosition)
          yPosition += 6
        })
        yPosition += 10
      }

      // Common Issues
      if (report.insights.commonIssues.length > 0) {
        pdf.setFontSize(14)
        pdf.setFont('helvetica', 'bold')
        pdf.text('Common Issues', margin, yPosition)
        yPosition += 8

        pdf.setFontSize(10)
        pdf.setFont('helvetica', 'normal')
        report.insights.commonIssues.forEach(issue => {
          pdf.text(`• ${issue}`, margin + 5, yPosition)
          yPosition += 6
        })
        yPosition += 10
      }

      // Improvement Suggestions
      if (report.insights.improvementSuggestions.length > 0) {
        pdf.setFontSize(14)
        pdf.setFont('helvetica', 'bold')
        pdf.text('Improvement Suggestions', margin, yPosition)
        yPosition += 8

        pdf.setFontSize(10)
        pdf.setFont('helvetica', 'normal')
        report.insights.improvementSuggestions.forEach(suggestion => {
          pdf.text(`• ${suggestion}`, margin + 5, yPosition)
          yPosition += 6
        })
        yPosition += 10
      }

      // Retrospective
      pdf.setFontSize(16)
      pdf.setFont('helvetica', 'bold')
      pdf.text('Retrospective', margin, yPosition)
      yPosition += 10

      // What Went Well
      if (report.retrospective.whatWentWell.length > 0) {
        pdf.setFontSize(12)
        pdf.setFont('helvetica', 'bold')
        pdf.text('What Went Well', margin, yPosition)
        yPosition += 8

        pdf.setFontSize(10)
        pdf.setFont('helvetica', 'normal')
        report.retrospective.whatWentWell.forEach(item => {
          pdf.text(`• ${item}`, margin + 5, yPosition)
          yPosition += 6
        })
        yPosition += 10
      }

      // What Went Wrong
      if (report.retrospective.whatWentWrong.length > 0) {
        pdf.setFontSize(12)
        pdf.setFont('helvetica', 'bold')
        pdf.text('What Went Wrong', margin, yPosition)
        yPosition += 8

        pdf.setFontSize(10)
        pdf.setFont('helvetica', 'normal')
        report.retrospective.whatWentWrong.forEach(item => {
          pdf.text(`• ${item}`, margin + 5, yPosition)
          yPosition += 6
        })
        yPosition += 10
      }

      // Action Items
      if (report.retrospective.actionItems.length > 0) {
        pdf.setFontSize(12)
        pdf.setFont('helvetica', 'bold')
        pdf.text('Action Items', margin, yPosition)
        yPosition += 8

        pdf.setFontSize(10)
        pdf.setFont('helvetica', 'normal')
        report.retrospective.actionItems.forEach(item => {
          pdf.text(`• ${item}`, margin + 5, yPosition)
          yPosition += 6
        })
      }

      // Footer
      const pageHeight = pdf.internal.pageSize.getHeight()
      pdf.setFontSize(8)
      pdf.setFont('helvetica', 'normal')
      pdf.text(`Generated on ${new Date().toLocaleDateString()}`, margin, pageHeight - 10)

      // Save the PDF
      const filename = `sprint-report-${sprintDetails.label.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`
      pdf.save(filename)
      
      console.log('✅ PDF exported successfully:', filename)
    } catch (error) {
      console.error('❌ PDF export failed:', error)
      throw new Error('Failed to generate PDF export')
    }
  }

  // Excel Export
  async exportToExcel(report: SprintReport, sprintDetails: SprintDetails): Promise<void> {
    try {
      console.log('📊 Generating Excel export...')
      
      const workbook = XLSX.utils.book_new()

      // Summary Sheet
      const summaryData = [
        ['Sprint Report Summary'],
        [''],
        ['Sprint Details'],
        ['Label', sprintDetails.label],
        ['Duration', sprintDetails.duration],
        ['Goal', sprintDetails.goal],
        ['Description', sprintDetails.description],
        [''],
        ['Metrics'],
        ['Total Cards', report.summary.totalCards],
        ['Completed Cards', report.summary.completedCards],
        ['Incomplete Cards', report.summary.incompleteCards],
        ['Velocity (cards/day)', report.summary.velocity],
        [''],
        ['Analysis Log'],
        ['Cards Found', report.analysisLog.dataFetching.cardsFound],
        ['Lists Found', report.analysisLog.dataFetching.listsFound],
        ['Comments Fetched', report.analysisLog.dataFetching.commentsFetched],
        ['Processing Time (ms)', report.analysisLog.processingTime.total]
      ]

      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData)
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary')

      // Insights Sheet
      const insightsData = [
        ['Insights'],
        [''],
        ['Bottlenecks'],
        ...report.insights.bottlenecks.map(b => [b]),
        [''],
        ['Common Issues'],
        ...report.insights.commonIssues.map(i => [i]),
        [''],
        ['Improvement Suggestions'],
        ...report.insights.improvementSuggestions.map(s => [s])
      ]

      const insightsSheet = XLSX.utils.aoa_to_sheet(insightsData)
      XLSX.utils.book_append_sheet(workbook, insightsSheet, 'Insights')

      // Retrospective Sheet
      const retrospectiveData = [
        ['Retrospective'],
        [''],
        ['What Went Well'],
        ...report.retrospective.whatWentWell.map(w => [w]),
        [''],
        ['What Went Wrong'],
        ...report.retrospective.whatWentWrong.map(w => [w]),
        [''],
        ['Action Items'],
        ...report.retrospective.actionItems.map(a => [a])
      ]

      const retrospectiveSheet = XLSX.utils.aoa_to_sheet(retrospectiveData)
      XLSX.utils.book_append_sheet(workbook, retrospectiveSheet, 'Retrospective')

      // List Distribution Sheet
      const listDistributionData = [
        ['List Distribution'],
        ['List Name', 'Card Count'],
        ...Object.entries(report.analysisLog.metricsCalculation.listDistribution).map(([list, count]) => [list, count])
      ]

      const listDistributionSheet = XLSX.utils.aoa_to_sheet(listDistributionData)
      XLSX.utils.book_append_sheet(workbook, listDistributionSheet, 'List Distribution')

      // Save the Excel file
      const filename = `sprint-report-${sprintDetails.label.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.xlsx`
      XLSX.writeFile(workbook, filename)
      
      console.log('✅ Excel exported successfully:', filename)
    } catch (error) {
      console.error('❌ Excel export failed:', error)
      throw new Error('Failed to generate Excel export')
    }
  }

  // PowerPoint Export
  async exportToPowerPoint(report: SprintReport, sprintDetails: SprintDetails): Promise<void> {
    try {
      console.log('📊 Generating PowerPoint export...')
      
      const pptx = new PptxGenJS()

      // Title Slide
      const titleSlide = pptx.addSlide()
      titleSlide.addText('Sprint Report', {
        x: 1, y: 1, w: 8, h: 1,
        fontSize: 32,
        bold: true,
        align: 'center'
      })
      titleSlide.addText(`${sprintDetails.label}`, {
        x: 1, y: 2.5, w: 8, h: 0.5,
        fontSize: 18,
        align: 'center'
      })
      titleSlide.addText(`Generated on ${new Date().toLocaleDateString()}`, {
        x: 1, y: 3.5, w: 8, h: 0.5,
        fontSize: 12,
        align: 'center',
        color: '666666'
      })

      // Sprint Summary Slide
      const summarySlide = pptx.addSlide()
      summarySlide.addText('Sprint Summary', {
        x: 0.5, y: 0.5, w: 9, h: 0.5,
        fontSize: 24,
        bold: true
      })

      const summaryData = [
        ['Metric', 'Value'],
        ['Total Cards', report.summary.totalCards.toString()],
        ['Completed Cards', report.summary.completedCards.toString()],
        ['Incomplete Cards', report.summary.incompleteCards.toString()],
        ['Velocity', `${report.summary.velocity} cards/day`]
      ]

      summarySlide.addTable(summaryData, {
        x: 0.5, y: 1.5, w: 9, h: 2,
        fontSize: 14,
        headerRowColor: '4472C4',
        headerRowFontColor: 'FFFFFF'
      })

      // Insights Slide
      if (report.insights.bottlenecks.length > 0 || report.insights.commonIssues.length > 0) {
        const insightsSlide = pptx.addSlide()
        insightsSlide.addText('Key Insights', {
          x: 0.5, y: 0.5, w: 9, h: 0.5,
          fontSize: 24,
          bold: true
        })

        let yPos = 1.5
        if (report.insights.bottlenecks.length > 0) {
          insightsSlide.addText('Bottlenecks:', {
            x: 0.5, y: yPos, w: 9, h: 0.3,
            fontSize: 16,
            bold: true,
            color: 'FF0000'
          })
          yPos += 0.4
          report.insights.bottlenecks.forEach(bottleneck => {
            insightsSlide.addText(`• ${bottleneck}`, {
              x: 0.8, y: yPos, w: 8.4, h: 0.3,
              fontSize: 12
            })
            yPos += 0.3
          })
          yPos += 0.2
        }

        if (report.insights.commonIssues.length > 0) {
          insightsSlide.addText('Common Issues:', {
            x: 0.5, y: yPos, w: 9, h: 0.3,
            fontSize: 16,
            bold: true,
            color: 'FF6600'
          })
          yPos += 0.4
          report.insights.commonIssues.forEach(issue => {
            insightsSlide.addText(`• ${issue}`, {
              x: 0.8, y: yPos, w: 8.4, h: 0.3,
              fontSize: 12
            })
            yPos += 0.3
          })
        }
      }

      // Retrospective Slide
      const retrospectiveSlide = pptx.addSlide()
      retrospectiveSlide.addText('Retrospective', {
        x: 0.5, y: 0.5, w: 9, h: 0.5,
        fontSize: 24,
        bold: true
      })

      let yPos = 1.5
      if (report.retrospective.whatWentWell.length > 0) {
        retrospectiveSlide.addText('What Went Well:', {
          x: 0.5, y: yPos, w: 4, h: 0.3,
          fontSize: 16,
          bold: true,
          color: '00AA00'
        })
        yPos += 0.4
        report.retrospective.whatWentWell.forEach(item => {
          retrospectiveSlide.addText(`• ${item}`, {
            x: 0.8, y: yPos, w: 3.9, h: 0.3,
            fontSize: 12
          })
          yPos += 0.3
        })
      }

      yPos = 1.5
      if (report.retrospective.whatWentWrong.length > 0) {
        retrospectiveSlide.addText('What Went Wrong:', {
          x: 5, y: yPos, w: 4, h: 0.3,
          fontSize: 16,
          bold: true,
          color: 'FF0000'
        })
        yPos += 0.4
        report.retrospective.whatWentWrong.forEach(item => {
          retrospectiveSlide.addText(`• ${item}`, {
            x: 5.3, y: yPos, w: 3.9, h: 0.3,
            fontSize: 12
          })
          yPos += 0.3
        })
      }

      // Action Items Slide
      if (report.retrospective.actionItems.length > 0) {
        const actionSlide = pptx.addSlide()
        actionSlide.addText('Action Items', {
          x: 0.5, y: 0.5, w: 9, h: 0.5,
          fontSize: 24,
          bold: true
        })

        let yPos = 1.5
        report.retrospective.actionItems.forEach((item, index) => {
          actionSlide.addText(`${index + 1}. ${item}`, {
            x: 0.5, y: yPos, w: 9, h: 0.3,
            fontSize: 14
          })
          yPos += 0.4
        })
      }

      // Save the PowerPoint file
      const filename = `sprint-report-${sprintDetails.label.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.pptx`
      await pptx.writeFile({ fileName: filename })
      
      console.log('✅ PowerPoint exported successfully:', filename)
    } catch (error) {
      console.error('❌ PowerPoint export failed:', error)
      throw new Error('Failed to generate PowerPoint export')
    }
  }

  // Shareable Link Generation
  generateShareableLink(report: SprintReport, sprintDetails: SprintDetails): string {
    // This would typically save the report to a database and return a shareable URL
    // For now, we'll create a simple data URL with the report summary
    const reportData = {
      sprint: sprintDetails.label,
      summary: report.summary,
      insights: {
        bottlenecks: report.insights.bottlenecks,
        commonIssues: report.insights.commonIssues,
        improvementSuggestions: report.insights.improvementSuggestions
      },
      retrospective: {
        whatWentWell: report.retrospective.whatWentWell,
        whatWentWrong: report.retrospective.whatWentWrong,
        actionItems: report.retrospective.actionItems
      },
      generatedAt: new Date().toISOString()
    }

    const dataUrl = `data:application/json;base64,${btoa(JSON.stringify(reportData))}`
    return dataUrl
  }
}

export const exportService = new ExportService() 